import { ReactNode } from 'react'
import { Box, Button, ButtonProps, Skeleton } from '@mui/material'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { type AllOrNone } from '@primitives/objects.utils'
import { ExternalLink } from '@ui/components/ExternalLink'
import { type ConnectionProps, ConnectWalletButton } from '@ui/features/connect-wallet/ConnectWalletButton'
import { Responsive } from '@ui/features/themes/basic-theme'
import { SizesAndSpaces } from '@ui/features/themes/design/1_sizes_spaces'
import { LlamaIcon } from '@ui/icons/LlamaIcon'
import { applySxProps } from '@ui/lib/mui'

const { Spacing, IconSize, MaxWidth, LineHeight } = SizesAndSpaces

type EmptyStateButtonProps = Omit<ButtonProps, 'type'> & {
  label?: ReactNode
  type?: 'button' | 'connect-wallet'
  testId?: string
}

export type EmptyStateCardProps = AllOrNone<ConnectionProps> & {
  title?: ReactNode
  description?: ReactNode
  icon?: ReactNode
  isLoading?: boolean
  size?: 'sm' | 'md'
  button?: EmptyStateButtonProps
  secondaryButton?: EmptyStateButtonProps
  testId?: string
}

const SIZE_CONFIG: Record<
  NonNullable<EmptyStateCardProps['size']>,
  { icon: Responsive; button: ButtonProps['size'] }
> = { sm: { icon: IconSize.lg, button: 'small' }, md: { icon: IconSize.xxl, button: 'medium' } }

const Skeletons = () => (
  <Stack sx={{ gap: Spacing.sm, width: MaxWidth.emptyStateCard }}>
    <Skeleton variant="rectangular" sx={{ height: LineHeight.sm }} />
    <Skeleton variant="rectangular" sx={{ height: LineHeight.xl }} />
  </Stack>
)

const getConnectionProps = (connectionProps: AllOrNone<ConnectionProps>): ConnectionProps => {
  if (!connectionProps.connect) throw new Error('Missing connectionProps for connect-wallet')
  return connectionProps
}

const EmptyStateButton = ({
  button,
  size,
  ...connectionProps
}: AllOrNone<ConnectionProps> & {
  button: NonNullable<EmptyStateCardProps['button']>
  size: NonNullable<EmptyStateCardProps['size']>
}) => {
  const { label, sx: buttonSx, testId, href, type = 'button', ...buttonProps } = button ?? {}
  const sharedProps = {
    ...buttonProps,
    variant: 'outlined',
    size: SIZE_CONFIG[size].button,
    href,
    sx: applySxProps({ alignSelf: 'center' }, buttonSx),
    ...(testId && { 'data-testid': testId }),
  } as const

  return type === 'connect-wallet' ? (
    <ConnectWalletButton {...sharedProps} {...getConnectionProps(connectionProps)} label={label} />
  ) : href?.startsWith('https') ? (
    <ExternalLink {...sharedProps} href={href} label={label} wide />
  ) : (
    <Button {...sharedProps}>{label}</Button>
  )
}

export const EmptyStateCard = ({
  title,
  description,
  icon,
  button,
  secondaryButton,
  isLoading,
  size = 'md',
  testId,
  ...connectionProps
}: EmptyStateCardProps) => (
  <Stack sx={{ gap: Spacing.xs, alignItems: 'center', maxWidth: MaxWidth.emptyStateCard }} data-testid={testId}>
    {icon || <LlamaIcon sx={{ width: SIZE_CONFIG[size].icon, height: SIZE_CONFIG[size].icon }} />}
    {isLoading ? (
      <Skeletons />
    ) : (
      <Stack sx={{ gap: Spacing.sm }}>
        <Stack sx={{ textAlign: 'center' }}>
          {title && (
            <Typography component="span" variant="headingXsBold">
              {title}
            </Typography>
          )}
          {description && (
            <Typography component="span" variant="bodySRegular" color="textSecondary" sx={{ overflowWrap: 'anywhere' }}>
              {description}
            </Typography>
          )}
        </Stack>
        {[button, secondaryButton].some(Boolean) && (
          <Box
            sx={{
              display: 'grid', // Using grid here for equal sized buttons
              gridTemplateColumns: button && secondaryButton ? { tablet: '1fr 1fr' } : undefined,
              gap: Spacing.xs,
              justifyContent: 'center',
            }}
          >
            {button && <EmptyStateButton button={button} size={size} {...connectionProps} />}
            {secondaryButton && <EmptyStateButton button={secondaryButton} size={size} {...connectionProps} />}
          </Box>
        )}
      </Stack>
    )}
  </Stack>
)
